import type { ArticleEditorValues } from './articles';

const storagePrefix = 'myblog.article-draft.v1';
const schemaVersion = 1;
const maxAgeMilliseconds = 24 * 60 * 60 * 1000;
const newDraftSessionKey = `${storagePrefix}:new-draft-id`;
const recoveryUserSessionKey = `${storagePrefix}:user-id`;

export function rememberRecoveryUser(userId: string): void {
  window.sessionStorage.setItem(recoveryUserSessionKey, userId);
}

export function getRememberedRecoveryUser(): string | null {
  return window.sessionStorage.getItem(recoveryUserSessionKey);
}

export interface ArticleDraftRecovery {
  schemaVersion: number;
  userId: string;
  draftKey: string;
  serverVersion: number | null;
  savedAt: number;
  values: ArticleEditorValues;
}

export function getNewDraftKey(): string {
  const existing = window.sessionStorage.getItem(newDraftSessionKey);
  if (existing) return existing;

  const created = crypto.randomUUID();
  window.sessionStorage.setItem(newDraftSessionKey, created);
  return created;
}

export function readDraftRecovery(userId: string, draftKey: string): ArticleDraftRecovery | null {
  const storageKey = getStorageKey(userId, draftKey);
  const serialized = window.localStorage.getItem(storageKey);
  if (!serialized) return null;

  try {
    const recovery = JSON.parse(serialized) as ArticleDraftRecovery;
    if (!isValidRecovery(recovery, userId, draftKey)) {
      window.localStorage.removeItem(storageKey);
      return null;
    }

    if (Date.now() - recovery.savedAt > maxAgeMilliseconds) {
      window.localStorage.removeItem(storageKey);
      return null;
    }

    return {
      ...recovery,
      values: {
        ...recovery.values,
        seriesId: recovery.values.seriesId ?? null,
        seriesPosition: recovery.values.seriesPosition ?? null,
      },
    };
  } catch {
    window.localStorage.removeItem(storageKey);
    return null;
  }
}

export function writeDraftRecovery(recovery: ArticleDraftRecovery): void {
  window.localStorage.setItem(
    getStorageKey(recovery.userId, recovery.draftKey),
    JSON.stringify(recovery),
  );
}

export function clearDraftRecovery(userId: string, draftKey: string): void {
  window.localStorage.removeItem(getStorageKey(userId, draftKey));
}

export function clearAllDraftRecoveries(): void {
  const keysToRemove: string[] = [];
  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index);
    if (key?.startsWith(`${storagePrefix}:`)) keysToRemove.push(key);
  }

  keysToRemove.forEach((key) => window.localStorage.removeItem(key));
  window.sessionStorage.removeItem(newDraftSessionKey);
  window.sessionStorage.removeItem(recoveryUserSessionKey);
}

function getStorageKey(userId: string, draftKey: string): string {
  return `${storagePrefix}:${encodeURIComponent(userId)}:${encodeURIComponent(draftKey)}`;
}

function isValidRecovery(
  recovery: ArticleDraftRecovery,
  userId: string,
  draftKey: string,
): boolean {
  return (
    recovery.schemaVersion === schemaVersion &&
    recovery.userId === userId &&
    recovery.draftKey === draftKey &&
    (recovery.serverVersion === null || Number.isInteger(recovery.serverVersion)) &&
    Number.isFinite(recovery.savedAt) &&
    typeof recovery.values.title === 'string' &&
    typeof recovery.values.slug === 'string' &&
    typeof recovery.values.excerpt === 'string' &&
    typeof recovery.values.bodyMarkdown === 'string' &&
    (recovery.values.coverMediaId === null || typeof recovery.values.coverMediaId === 'string') &&
    (recovery.values.seoTitle === null || typeof recovery.values.seoTitle === 'string') &&
    (recovery.values.seoDescription === null ||
      typeof recovery.values.seoDescription === 'string') &&
    (recovery.values.seriesId === undefined ||
      recovery.values.seriesId === null ||
      typeof recovery.values.seriesId === 'string') &&
    (recovery.values.seriesPosition === undefined ||
      recovery.values.seriesPosition === null ||
      Number.isInteger(recovery.values.seriesPosition))
  );
}
